import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, Circle,
  useMap, useMapEvents, LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './FlatMapComponent.css';
import { globeService } from '../services/globeService';
import { normalizeLng, unwrapLng, unwrapLngs, sliceUnwrappedSegments } from '../utils/geo';
import { LIGHT_BASE, DARK_BASE } from '../utils/basemaps';

// Re-anchored route leg for the planned sea line: it ALWAYS starts at the
// vessel's LIVE position and ends at the discharge PORT CODE (a canonical
// UN/LOCODE resolved via /resolve-port?routing=1). Routing by code lets NavAPI
// use its own navigable coordinates for the discharge, which fixes both the
// Mapbox-garbage and our-bad-DB-coord problems (we never feed it a discharge
// lat/lon). Returns null — and therefore NO line is drawn — unless the vessel
// has a live position AND the discharge resolved to a real port code.
const vesselRouteLeg = (v) => {
  if (v?.latitude == null || v?.longitude == null) return null;
  const discharge = (v?.ports || []).find(p => p.type === 'discharge' && p.routeCode);
  if (!discharge) return null;
  return {
    start: { lat: v.latitude, lon: v.longitude },
    dischargeCode: discharge.routeCode,
    dischargeName: discharge.name || '',
  };
};

// Route legs are keyed by live-start coords (rounded) + discharge code. Because
// the start is the ship's live position, each vessel gets its own line; the key
// still dedupes the (rare) case of two vessels at the same fix bound for the
// same discharge, and lets SECA partitions cache per leg.
const legKey = (leg) =>
  `${leg.start.lat.toFixed(3)},${leg.start.lon.toFixed(3)}->${leg.dischargeCode}`;

// Estimated sailed leg: the coord->coord line from the LOADING port to the
// vessel's LIVE position, i.e. roughly where it has already been. Requires a
// live position AND a loading port with resolved (routing=1, i.e. genuinely
// navigable) coords. Returns null while the vessel is still at/near the load
// port (nothing sailed yet) so we never draw a zero-length stub. Unlike the
// planned leg this is a pure coord->coord route (loading has no routeCode).
const sailedRouteLeg = (v) => {
  if (v?.latitude == null || v?.longitude == null) return null;
  const loading = (v?.ports || []).find(p => p.type === 'loading');
  if (loading?.lat == null || loading?.lon == null) return null;
  // Still on the load berth: no meaningful sailed track yet.
  const dLat = Math.abs(loading.lat - v.latitude);
  const dLon = Math.abs(normalizeLng(loading.lon - v.longitude));
  if (dLat < 0.03 && dLon < 0.03) return null;
  return {
    start: { lat: loading.lat, lon: loading.lon },
    end: { lat: v.latitude, lon: v.longitude },
  };
};

// Sailed legs live in their own key namespace so they never collide with the
// planned-leg keys in the shared `requested` dedup set or the batch response
// map. Keyed by start (loading) coords -> end (live) coords, both rounded.
const sailedLegKey = (leg) =>
  `sailed:${leg.start.lat.toFixed(3)},${leg.start.lon.toFixed(3)}>` +
  `${leg.end.lat.toFixed(3)},${leg.end.lon.toFixed(3)}`;

const NAV_STATUS_COLORS = {
  0: '#28a745', 1: '#ffc107', 2: '#dc2626', 3: '#fd7e14', 4: '#fd7e14',
  5: '#4a6b25', 6: '#dc2626', 7: '#6f42c1', 8: '#28a745',
};

const NAV_STATUS_LABELS = {
  0: 'Under way', 1: 'At anchor', 2: 'Not under command', 3: 'Restricted',
  4: 'Constrained by draught', 5: 'Moored', 6: 'Aground', 7: 'Fishing',
  8: 'Sailing', 14: 'AIS-SART', 15: 'Undefined',
};

const DEFAULT_CENTER = [20, 0];
const DEFAULT_ZOOM = 2;
const FLYTO_ZOOM = 7;

// Hard latitude clamp for the pan wall. Web-Mercator tiles top out near
// +/-85.05 deg; we clamp a touch inside so the maxBounds never exposes grey
// void above/below the tiled world.
const MAX_LAT = 85;
// Longitude wall for the THREE-world canvas (the MarineTraffic model): the
// central world [-180, 180] plus ONE neighbouring copy each way, so the user can
// pan left/right into a single adjacent world and then hits a hard wall at
// +/-540. Markers (vessels + port dots) are stamped into all three copies so
// they stay visible whichever copy is on screen; a route line is drawn ONCE,
// unwrapped from its anchored (central-world) start, and may naturally extend
// past +/-180 into the neighbouring copy that this wall keeps reachable.
const WORLD_LON = 540;
const WORLD_BOUNDS = L.latLngBounds([MAX_LAT, -WORLD_LON], [-MAX_LAT, WORLD_LON]);
// Pragmatic floor on how far the user may zoom out. MarineTraffic shows up to
// ~3 worlds at min zoom, so this only guards a tiny/zero-size container: the
// real floor is Leaflet's getBoundsZoom for the ±540 window (see FleetMaxBounds),
// which lets the whole three-world canvas be visible at low zoom.
const MIN_ZOOM_FLOOR = 2;

// Route line colours: normal planned route vs. legs that fall inside a SECA
// (Sulphur Emission Control Area). SECA legs are drawn thicker + red-orange so
// the desk can see at a glance where the low-sulphur burn applies.
const ROUTE_COLOR = '#4a6b25';
const SECA_COLOR = '#e8590c';
// Estimated sailed route (loading port -> live position): the same blue as the
// planned route but very muted, so it reads as "already behind the ship" and
// never competes with the crisp planned line ahead of it. Solid (dashed is
// taken by the actual-track breadcrumb).
const SAILED_ROUTE_OPACITY = 0.28;

// On-demand actual-track pull: recent window only. Token cost = (days + 1),
// so this stays deliberately small and is never fetched automatically.
const TRACK_HISTORY_DAYS = 3;

// Formats a Date as ISO 8601 without milliseconds, which is what the
// vessel-history endpoint (and NavAPI beneath it) requires.
const isoNoMillis = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

// Readable "12 Aug 14:30" from an ISO timestamp; '' when missing/invalid so
// the caller can hide the field entirely (no "—" spam).
const fmtDateTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Integer with thousands separators, or '' when not a finite number.
const fmtInt = (val) => {
  const n = Number(val);
  if (!isFinite(n)) return '';
  return Math.round(n).toLocaleString('en-GB');
};

// Compact "4d 6h" / "2d" / "5h" elapsed since an ISO timestamp. Returns ''
// for missing/invalid/future timestamps so dwell fields self-hide.
const durationSince = (val) => {
  if (!val) return '';
  const then = new Date(val);
  if (isNaN(then.getTime())) return '';
  const ms = Date.now() - then.getTime();
  if (ms < 0) return '';
  const totalHours = Math.floor(ms / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return `${hours}h`;
  return '<1h';
};

// Compact "Route 3,936 nm · SECA 2,961 nm (75%)" from a route.data object, or
// '' when neither distance is present. When both distances are finite and
// positive the SECA share is appended as a percentage.
const fmtRouteDistance = (routeData) => {
  if (!routeData) return '';
  const totalN = Number(routeData.total_distance);
  const secaN = Number(routeData.seca_distance);
  const total = fmtInt(totalN);
  const seca = fmtInt(secaN);
  const parts = [];
  if (total) parts.push(`Route ${total} nm`);
  if (seca) {
    let secaPart = `SECA ${seca} nm`;
    if (isFinite(totalN) && totalN > 0 && isFinite(secaN) && secaN > 0) {
      secaPart += ` (${Math.round((secaN / totalN) * 100)}%)`;
    }
    parts.push(secaPart);
  }
  return parts.join(' · ');
};

// Mirror of getVesselKey() in ShipmentOverviewTab. Kept as a small local
// helper rather than imported because FleetMapComponent is also used
// elsewhere with simpler vessel shapes.
const vesselKeyOf = (v) => {
  if (!v) return null;
  if (v.deliveryId) return `d:${v.deliveryId}`;
  if (v.watchlistEntryId) return `w:${v.watchlistEntryId}`;
  if (v.imo) return `i:${v.imo}`;
  if (v.mmsi) return `m:${v.mmsi}`;
  return null;
};

// Vessel markers are coloured by SANCTIONS / COMPLIANCE, not AIS nav-status:
// green = cleared (or not yet screened), red = sanctioned or not cleared. The
// AIS nav-status is still surfaced in the marker popup badge. `isFlagged` is
// derived upstream (sanctions_status === 'flagged' OR a risk/"warning" hit).
const VESSEL_OK_COLOR = '#28a745';
const VESSEL_FLAGGED_COLOR = '#dc2626';

const buildVesselIcon = (heading, isFlagged, isHighlighted) => {
  const color = isFlagged ? VESSEL_FLAGGED_COLOR : VESSEL_OK_COLOR;
  const rotation = heading || 0;
  const size = isHighlighted ? 38 : 28;
  const strokeWidth = isHighlighted ? 2.5 : 1.5;
  const half = size / 2;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-12 -14 24 28" width="${size}" height="${size + 4}">
      <g transform="rotate(${rotation} 0 0)">
        <polygon points="0,-12 8,10 0,5 -8,10" fill="${color}" stroke="#fff" stroke-width="${strokeWidth}"/>
      </g>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: `flat-map-vessel-icon${isHighlighted ? ' flat-map-vessel-icon-highlighted' : ''}`,
    iconSize: [size, size + 4],
    iconAnchor: [half, (size + 4) / 2],
  });
};

const buildPortIcon = (portKind, isHighlighted) => {
  const color = portKind === 'loading' ? '#28a745' : portKind === 'discharge' ? '#dc2626' : '#4a6b25';
  const size = isHighlighted ? 20 : 14;
  const cx = size / 2;
  const ringR = isHighlighted ? 8 : 6;
  const dotR = isHighlighted ? 3.5 : 2.5;
  const stroke = isHighlighted ? 2.25 : 1.5;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${cx}" cy="${cx}" r="${ringR}" fill="none" stroke="${color}" stroke-width="${stroke}"/>
      <circle cx="${cx}" cy="${cx}" r="${dotR}" fill="${color}"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: `flat-map-port-icon${isHighlighted ? ' flat-map-port-icon-highlighted' : ''}`,
    iconSize: [size, size],
    iconAnchor: [cx, cx],
  });
};

// Discovered (AIS search) vessels are drawn as a DISTINCT diamond in a magenta
// accent so they never read as a delivery/watchlist vessel (arrow) or a port
// dot (ring). They are ad-hoc search hits, not tracked hulls. The diamond is
// rotated to the vessel heading like the fleet arrows, but the shape + colour
// keep them unmistakably separate.
const DISCOVERY_COLOR = '#a21caf';

const buildDiscoveredIcon = (heading) => {
  const size = 22;
  const rotation = heading || 0;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-12 -12 24 24" width="${size}" height="${size}">
      <g transform="rotate(${rotation} 0 0)">
        <path d="M0,-9 L7,0 L0,9 L-7,0 Z" fill="${DISCOVERY_COLOR}" stroke="#fff" stroke-width="1.75"/>
        <circle cx="0" cy="0" r="1.6" fill="#fff"/>
      </g>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'flat-map-discovered-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// The search-centre pin for a "vessels within range" search: a hollow magenta
// crosshair so the origin of the radius circle is obvious.
const buildDiscoveryCentreIcon = () => {
  const size = 20;
  const c = size / 2;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${c}" cy="${c}" r="7" fill="none" stroke="${DISCOVERY_COLOR}" stroke-width="2"/>
      <line x1="${c}" y1="0" x2="${c}" y2="4" stroke="${DISCOVERY_COLOR}" stroke-width="2"/>
      <line x1="${c}" y1="${size - 4}" x2="${c}" y2="${size}" stroke="${DISCOVERY_COLOR}" stroke-width="2"/>
      <line x1="0" y1="${c}" x2="4" y2="${c}" stroke="${DISCOVERY_COLOR}" stroke-width="2"/>
      <line x1="${size - 4}" y1="${c}" x2="${size}" y2="${c}" stroke="${DISCOVERY_COLOR}" stroke-width="2"/>
      <circle cx="${c}" cy="${c}" r="1.5" fill="${DISCOVERY_COLOR}"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'flat-map-discovery-centre-icon',
    iconSize: [size, size],
    iconAnchor: [c, c],
  });
};

// Lets the "vessels within range" tool set its centre by clicking the map.
// Only active while pickActive is true; reports the clicked [lat,lon] up and
// leaves the map otherwise untouched (no effect on existing pan/zoom/markers).
const DiscoveryPicker = ({ pickActive, onPick }) => {
  useMapEvents({
    click: (e) => {
      if (pickActive && onPick) onPick({ lat: e.latlng.lat, lon: e.latlng.lng });
    },
  });
  return null;
};

// One-shot fit over discovered vessels (+ centre), driven by a timestamp so
// each new search re-frames the map on the hits. Does not touch fleet bounds.
const DiscoveryFit = ({ discoveredVessels, discoveryCenter, trigger }) => {
  const map = useMap();
  const lastTs = useRef(0);
  useEffect(() => {
    if (!trigger || trigger === lastTs.current) return;
    lastTs.current = trigger;
    const pts = [];
    (discoveredVessels || []).forEach(v => {
      if (v.latitude != null && v.longitude != null) pts.push([v.latitude, v.longitude]);
    });
    if (discoveryCenter?.lat != null && discoveryCenter?.lon != null) {
      pts.push([discoveryCenter.lat, discoveryCenter.lon]);
    }
    if (pts.length === 0) return;
    if (pts.length === 1) { map.flyTo(pts[0], 8, { duration: 1.0 }); return; }
    map.flyToBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 10, duration: 1.0 });
  }, [map, trigger, discoveredVessels, discoveryCenter]);
  return null;
};

// One-shot fly to a single discovered vessel when the user clicks its result row.
const DiscoveryFlyTo = ({ discoveryFocus }) => {
  const map = useMap();
  const lastTs = useRef(0);
  useEffect(() => {
    if (!discoveryFocus || !discoveryFocus.ts) return;
    if (discoveryFocus.ts === lastTs.current) return;
    lastTs.current = discoveryFocus.ts;
    if (discoveryFocus.lat != null && discoveryFocus.lon != null) {
      map.flyTo([discoveryFocus.lat, discoveryFocus.lon], 9, { duration: 1.0 });
    }
  }, [map, discoveryFocus]);
  return null;
};

// Canonical points: every vessel + port folded into the CENTRAL world
// [-180, 180] via normalizeLng. On the three-world (±540) canvas the markers are
// stamped into all three copies, but the fit anchors on the central copy so the
// framing is stable regardless of which copy the user last panned to.
const canonicalPoints = (vessels, portMarkers) => {
  const pts = [];
  vessels.forEach(v => {
    if (v.latitude != null && v.longitude != null) pts.push([v.latitude, normalizeLng(v.longitude)]);
  });
  portMarkers.forEach(pm => {
    if (pm.lat != null && pm.lon != null) pts.push([pm.lat, normalizeLng(pm.lon)]);
  });
  return pts;
};

// Voyage-extent points for the fit: for every routed vessel, the route drawn on
// the map is anchored to its live-start longitude NORMALIZED into the central
// world and then unwrapped from there (identical to what FleetRoutes renders), so
// the discharge/route can legitimately run past +/-180 into the neighbouring copy
// the ±540 canvas exposes. Feeding those SAME unwrapped points into the fit means
// the frame covers the whole voyage the user sees (e.g. Japan → Balboa's +281
// discharge), not just the vessel's own fix. routeWaypointsByLeg maps legKey ->
// raw [lat,lon] waypoints (reported up from FleetRoutes).
const routeExtentPoints = (vessels, routeWaypointsByLeg) => {
  const pts = [];
  if (!routeWaypointsByLeg) return pts;
  vessels.forEach(v => {
    const leg = vesselRouteLeg(v);
    if (leg) {
      const wps = routeWaypointsByLeg[legKey(leg)];
      if (wps && wps.length >= 2) {
        // Anchor to the vessel's live-start lon — the same frame the planned
        // line is drawn in.
        unwrapLngs(normalizeLng(leg.start.lon), wps).forEach(p => pts.push(p));
      }
    }
    // Sailed leg (loading -> live position). It is anchored to the SAME frame as
    // the planned leg (the live-position lon) so both legs meet at the marker;
    // include its waypoints so the fit frames the whole visible voyage.
    const sailed = sailedRouteLeg(v);
    if (sailed) {
      const wps = routeWaypointsByLeg[sailedLegKey(sailed)];
      if (wps && wps.length >= 2) {
        unwrapLngs(normalizeLng(sailed.end.lon), wps).forEach(p => pts.push(p));
      }
    }
  });
  return pts;
};

// Clamp a longitude into the ±540 canvas so a unioned multi-voyage frame can
// never ask Leaflet to fit past the pan wall.
const clampLon = (lon) => Math.max(-WORLD_LON, Math.min(WORLD_LON, lon));

// Fits the map over the SAME geometry that is rendered: canonical (central-world)
// vessel + port points UNIONED with each routed vessel's unwrapped voyage extent.
// Longitudes are clamped to the ±540 wall. Used by both the auto-fit (on
// vessel-list change) and the manual "Show All" button.
const fitVesselsAndPorts = (map, vessels, portMarkers, routeWaypointsByLeg) => {
  const points = [
    ...canonicalPoints(vessels, portMarkers),
    ...routeExtentPoints(vessels, routeWaypointsByLeg),
  ].map(([lat, lon]) => [lat, clampLon(lon)]);

  if (points.length === 0) return;
  if (points.length === 1) { map.flyTo(points[0], 6, { duration: 1.2 }); return; }
  map.flyToBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 8, duration: 1.2 });
};

const FleetFitBounds = ({ vessels, portMarkers, routeWaypointsByLeg }) => {
  const map = useMap();
  const lastKey = useRef('');

  const boundsKey = useMemo(() => {
    const parts = vessels.map(v => v.latitude != null ? `${v.imo || v.mmsi}` : '').filter(Boolean);
    // Include the set of routed legs so the frame re-fits once a route arrives
    // (the voyage extent can push the bounds well past the vessel's own fix).
    const routeKeys = routeWaypointsByLeg ? Object.keys(routeWaypointsByLeg).sort().join(',') : '';
    return parts.join('|') + '|' + portMarkers.length + '|' + routeKeys;
  }, [vessels, portMarkers, routeWaypointsByLeg]);

  useEffect(() => {
    if (!boundsKey || boundsKey === lastKey.current) return;
    lastKey.current = boundsKey;
    fitVesselsAndPorts(map, vessels, portMarkers, routeWaypointsByLeg);
  }, [map, boundsKey, vessels, portMarkers, routeWaypointsByLeg]);

  return null;
};

// Locks the map to the THREE-world canvas [-540, 540] (the MarineTraffic model):
// the user can pan one world copy each way and then hits a hard wall. This is a
// FIXED wall — it does not depend on the vessels/ports at all, so it engages
// UNCONDITIONALLY on map ready (plain mount included) and re-applies on every
// container resize so the minZoom floor tracks the current container width.
// maxBoundsViscosity 1.0 makes it a hard wall (no elastic bounce past the edge).
// The TileLayers are left wrapping so tiles render seamlessly across all copies.
const FleetMaxBounds = () => {
  const map = useMap();

  useEffect(() => {
    const apply = () => {
      map.setMaxBounds(WORLD_BOUNDS);
      map.options.maxBoundsViscosity = 1.0;
      // Floor the zoom at the level that fits the full ±540 canvas in the
      // current container, so a pinch-zoom-out cannot reveal grey void past the
      // wall while still letting the whole three-world canvas be visible at min
      // zoom (as MarineTraffic does). getBoundsZoom(inside=true) returns the
      // largest zoom whose extent still fits the window; never go below
      // MIN_ZOOM_FLOOR (guards a tiny/zero-size container). Recomputed on resize.
      const fitZoom = map.getBoundsZoom(WORLD_BOUNDS, true);
      const minZoom = Math.max(MIN_ZOOM_FLOOR, Math.ceil(fitZoom));
      map.setMinZoom(minZoom);
      if (map.getZoom() < minZoom) map.setZoom(minZoom);
    };

    // Engage immediately on mount (map is ready when this effect runs), then
    // re-apply on every resize.
    apply();
    map.on('resize', apply);
    return () => { map.off('resize', apply); };
  }, [map]);

  return null;
};

// Manual fit-all trigger. Watches a timestamp; whenever it increments,
// re-runs the same bounds calc the auto-fit uses. Lets the user pull back
// to the full view after they've zoomed into a single vessel.
const FleetFitAll = ({ vessels, portMarkers, routeWaypointsByLeg, trigger }) => {
  const map = useMap();
  const lastTs = useRef(0);

  useEffect(() => {
    if (!trigger || trigger === lastTs.current) return;
    lastTs.current = trigger;
    fitVesselsAndPorts(map, vessels, portMarkers, routeWaypointsByLeg);
  }, [map, trigger, vessels, portMarkers, routeWaypointsByLeg]);

  return null;
};

// One-shot fly handler driven by `flyTrigger.ts`. Fires whenever the
// timestamp changes, so clicking the same card twice still re-centers.
const FleetFlyTo = ({ vessels, flyTrigger }) => {
  const map = useMap();
  const lastTs = useRef(0);

  useEffect(() => {
    if (!flyTrigger || !flyTrigger.ts || !flyTrigger.key) return;
    if (flyTrigger.ts === lastTs.current) return;
    lastTs.current = flyTrigger.ts;
    const v = vessels.find(x => vesselKeyOf(x) === flyTrigger.key);
    if (v?.latitude != null && v?.longitude != null) {
      map.flyTo([v.latitude, v.longitude], FLYTO_ZOOM, { duration: 1.0 });
    }
  }, [map, flyTrigger, vessels]);

  return null;
};

// Draws the navigable sea route for every delivery vessel that has a live
// position AND a discharge resolved to a real port code. The line is
// RE-ANCHORED: it starts at the vessel's LIVE position and ends at the discharge
// PORT CODE (never a discharge lat/lon), so the ship always sits on its own
// line and NavAPI routes the discharge from its own navigable coords. Routes are
// fetched in ONE batched round-trip (MultipleRoutes), deduped by leg key and
// cached server-side so the dashboard stays token-light. Each fetched route is
// then partitioned into SECA / non-SECA legs (FREE, no token) and re-rendered as
// per-segment coloured Polylines. SECA segments are cached per legKey so they
// are never refetched.
// Route styles react to the shared hover/select key: the hovered vessel's
// voyage pops (thicker, full opacity, drawn last so it sits on top) while
// every other route dims right down — that contrast is what lets the desk
// tell tangled routes apart. The lines are themselves hoverable, so mousing
// over any route lights up its vessel, ports, and card via onHoverVessel.
const ROUTE_STYLE = {
  planned: {
    normal: { color: ROUTE_COLOR, weight: 2, opacity: 0.5 },
    highlighted: { color: ROUTE_COLOR, weight: 4, opacity: 0.95 },
    dimmed: { color: ROUTE_COLOR, weight: 2, opacity: 0.12 },
  },
  seca: {
    normal: { color: SECA_COLOR, weight: 3, opacity: 0.75 },
    highlighted: { color: SECA_COLOR, weight: 5, opacity: 0.95 },
    dimmed: { color: SECA_COLOR, weight: 3, opacity: 0.18 },
  },
  sailed: {
    normal: { color: ROUTE_COLOR, weight: 2, opacity: SAILED_ROUTE_OPACITY },
    highlighted: { color: ROUTE_COLOR, weight: 3, opacity: 0.65 },
    dimmed: { color: ROUTE_COLOR, weight: 2, opacity: 0.08 },
  },
};

const routeStyleFor = (family, vKey, hoveredVesselKey) => {
  if (!hoveredVesselKey) return ROUTE_STYLE[family].normal;
  return vKey === hoveredVesselKey
    ? ROUTE_STYLE[family].highlighted
    : ROUTE_STYLE[family].dimmed;
};

const FleetRoutes = ({
  vessels, onRouteData = null, hoveredVesselKey = null, onHoverVessel = null,
}) => {
  // key -> { waypoints, total_distance, seca_distance }
  const [routes, setRoutes] = useState({});
  // key -> { segments: [{waypoints, is_seca}], total_distance, seca_distance }
  const [secaByLeg, setSecaByLeg] = useState({});
  const requested = useRef(new Set());
  const secaRequested = useRef(new Set());

  // --- Phase 1: fetch any not-yet-requested routes in batched calls. ---
  useEffect(() => {
    let cancelled = false;

    // Collect the unique, not-yet-requested legs for this vessel set. Two leg
    // families share this pass (and the `requested` dedup set): the PLANNED leg
    // (live position -> discharge port code, see vesselRouteLeg) rendered as the
    // crisp blue line ahead of the ship, and the SAILED leg (loading coords ->
    // live position coords, see sailedRouteLeg) rendered muted behind it. Both
    // are coord/port legs the batch endpoint validates per-endpoint, so they go
    // out together and just carry different batch payloads.
    const pending = [];
    const seen = new Set();
    vessels.forEach((v) => {
      const leg = vesselRouteLeg(v);
      if (leg) {
        const key = legKey(leg);
        if (!requested.current.has(key) && !seen.has(key)) {
          seen.add(key);
          pending.push({
            key,
            payload: {
              startLat: leg.start.lat, startLon: leg.start.lon,
              endPortCode: leg.dischargeCode,
            },
          });
        }
      }
      const sailed = sailedRouteLeg(v);
      if (sailed) {
        const key = sailedLegKey(sailed);
        if (!requested.current.has(key) && !seen.has(key)) {
          seen.add(key);
          pending.push({
            key,
            payload: {
              startLat: sailed.start.lat, startLon: sailed.start.lon,
              endLat: sailed.end.lat, endLon: sailed.end.lon,
            },
          });
        }
      }
    });
    if (pending.length === 0) return undefined;
    pending.forEach(p => requested.current.add(p.key));

    const applyRoute = (key, data) => {
      const wps = data?.waypoints || [];
      if (!cancelled && wps.length > 1) {
        setRoutes(prev => ({ ...prev, [key]: data }));
      }
      // Report the full route data (total_distance / seca_distance) up so the
      // vessel popup can show the voyage/SECA nm without a second fetch.
      if (!cancelled && onRouteData && data) onRouteData(key, data);
    };

    // The batch endpoint caps at 16 legs/call, so split the combined leg list
    // into ≤16-leg chunks. Each chunk is its own POST; a failed chunk releases
    // ONLY its own keys for retry (the others may still have succeeded).
    const CHUNK = 16;
    for (let i = 0; i < pending.length; i += CHUNK) {
      const chunk = pending.slice(i, i + CHUNK);
      globeService.getSeaRoutesBatch(chunk.map(p => p.payload)).then((res) => {
        if (!res?.configured) return; // routing disabled -> no lines
        const data = res.data || [];
        // Batch responses come back in the SAME order the legs were sent.
        chunk.forEach(({ key }, j) => {
          if (data[j]) applyRoute(key, data[j]);
        });
      }).catch(() => {
        // This chunk failed: let its legs be retried on a later vessel change.
        chunk.forEach(p => requested.current.delete(p.key));
      });
    }

    return () => { cancelled = true; };
  }, [vessels, onRouteData]);

  // --- Phase 2: for each fetched route, partition it into SECA / non-SECA legs
  // (free geometry call) so the line can be coloured. Cached per legKey. ---
  useEffect(() => {
    let cancelled = false;
    Object.entries(routes).forEach(([key, data]) => {
      // Sailed legs are drawn as a single muted line — no SECA segmentation.
      if (key.startsWith('sailed:')) return;
      if (secaRequested.current.has(key)) return;
      const wps = data?.waypoints || [];
      if (wps.length < 2) return;
      secaRequested.current.add(key);
      globeService.getSecaSegments(wps).then((res) => {
        if (cancelled || !res?.configured) return;
        const segments = res.segments || [];
        if (segments.length === 0) return;
        setSecaByLeg(prev => ({ ...prev, [key]: res }));
        // Enrich the popup readout with the (more precise) segmented totals so
        // the SECA percentage is exact. Carry the phase-1 waypoints through
        // unchanged so the parent's fit (which reads waypoints off this report)
        // is not clobbered by the SECA re-report.
        if (onRouteData && res.total_distance != null) {
          onRouteData(key, {
            waypoints: wps,
            total_distance: res.total_distance,
            seca_distance: res.seca_distance,
          });
        }
      }).catch(() => {
        // Free call: on failure the route just stays mono-colour.
        secaRequested.current.delete(key);
      });
    });
    return () => { cancelled = true; };
  }, [routes, onRouteData]);

  // Two buckets: the hovered/selected vessel's lines render AFTER everyone
  // else's, so the highlighted voyage always sits on top of the tangle.
  const lines = [];
  const highlightedLines = [];
  vessels.forEach((v, vi) => {
    const vKey = vesselKeyOf(v);
    const isHL = hoveredVesselKey != null && vKey === hoveredVesselKey;
    const bucket = isHL ? highlightedLines : lines;
    // Hovering any of this vessel's route lines highlights the whole voyage
    // (vessel arrow, ports, card included) via the shared hover key.
    const hoverHandlers = onHoverVessel
      ? {
          mouseover: () => onHoverVessel(vKey),
          mouseout: () => onHoverVessel(null),
        }
      : undefined;

    // Estimated sailed leg (loading -> live position), drawn FIRST so it sits
    // behind the crisp planned line. Muted solid blue, no SECA colouring. It is
    // unwrapped from the SAME anchor as the planned leg (the live-position lon
    // folded into the central world) so the two legs meet exactly at the marker.
    const sailed = sailedRouteLeg(v);
    if (sailed) {
      const sRoute = routes[sailedLegKey(sailed)];
      if (sRoute && (sRoute.waypoints || []).length > 1) {
        const sAnchor = normalizeLng(sailed.end.lon);
        bucket.push(
          <Polyline
            key={`fleet-sailed-${vi}`}
            positions={unwrapLngs(sAnchor, sRoute.waypoints || [])}
            pathOptions={routeStyleFor('sailed', vKey, hoveredVesselKey)}
            eventHandlers={hoverHandlers}
          />
        );
      }
    }

    const leg = vesselRouteLeg(v);
    if (!leg) return;
    const key = legKey(leg);
    const route = routes[key];
    if (!route) return;
    const seca = secaByLeg[key];
    // Anchor the route to the CENTRAL world: fold the live-start longitude into
    // [-180, 180] so the whole voyage is unwrapped from the central copy. The
    // line is drawn ONCE and left continuous — it may run past +/-180 into the
    // one neighbouring copy the ±540 canvas exposes (that is exactly where the
    // stamped discharge dot's +360 copy sits), so it is never edge-cut and never
    // repeated in every world.
    const anchor = normalizeLng(leg.start.lon);

    if (seca && seca.segments && seca.segments.length > 0) {
      // Coloured per-segment rendering: SECA legs red-orange + thicker, the rest
      // normal blue. The whole route is unwrapped ONCE into a single continuous
      // frame (anchored to the normalized live-start lon) and then sliced back
      // into per-segment ranges (see sliceUnwrappedSegments): every colour break
      // lands in the same frame by construction, so the segments meet cleanly
      // across the antimeridian with no running anchor to drift.
      sliceUnwrappedSegments(anchor, seca.segments).forEach((slice, segIdx) => {
        bucket.push(
          <Polyline
            key={`fleet-route-${vi}-seca-${segIdx}`}
            positions={slice.latlngs}
            pathOptions={routeStyleFor(slice.is_seca ? 'seca' : 'planned', vKey, hoveredVesselKey)}
            eventHandlers={hoverHandlers}
          />
        );
      });
    } else {
      // Segments not (yet) available: draw the whole route in the normal colour,
      // unwrapped into one continuous line (anchored to the normalized start).
      const unwrapped = unwrapLngs(anchor, route.waypoints || []);
      bucket.push(
        <Polyline
          key={`fleet-route-${vi}`}
          positions={unwrapped}
          pathOptions={routeStyleFor('planned', vKey, hoveredVesselKey)}
          eventHandlers={hoverHandlers}
        />
      );
    }
  });
  return <>{lines}{highlightedLines}</>;
};


const FleetMapComponent = ({
  vessels = [],
  portMarkers = [],
  hoveredVesselKey = null,
  flyTrigger = null,
  fitAllTrigger = 0,
  onHoverVessel = null,
  onVesselClick = null,
  // ── AIS discovery (search) overlay ─────────────────────────────────
  discoveredVessels = [],       // ad-hoc search hits, plotted as magenta diamonds
  discoveryCenter = null,       // { lat, lon } centre of a within-range search
  discoveryRadiusKm = null,     // radius (km) → drawn as a translucent circle
  discoveryPickActive = false,  // "click the map to set the centre" mode
  discoveryFocus = null,        // { lat, lon, ts } → fly to one discovered vessel
  discoveryFitTrigger = 0,      // bump to re-frame the map over the search hits
  onDiscoveryPick = null,       // (point:{lat,lon}) => void
}) => {
  const mapRef = useRef(null);

  // Per-vessel actual (AIS) tracks, keyed by vesselKeyOf(). A present entry
  // means the breadcrumb overlay is ON; a second click removes the key to
  // toggle it OFF. `trackLoading` holds the key currently being fetched.
  const [tracks, setTracks] = useState({});
  const [trackLoading, setTrackLoading] = useState(null);
  // { key, message } for the last failed/empty pull, so the error only shows
  // in the popup of the vessel it belongs to (not every open popup).
  const [trackError, setTrackError] = useState(null);
  // Route distances (total_distance / seca_distance) keyed by legKey, reported
  // up from FleetRoutes so the vessel popup can show voyage/SECA nm. The SECA
  // partition (phase 2) reports a second, more precise reading for the same key,
  // so we overwrite rather than keep-first here.
  const [routeDataByLeg, setRouteDataByLeg] = useState({});
  const handleRouteData = useCallback((key, data) => {
    setRouteDataByLeg(prev => ({ ...prev, [key]: data }));
  }, []);
  // legKey -> raw [lat,lon] waypoints, derived from the reported route data so
  // the auto-fit / Show-All can frame the whole voyage extent (unwrapped from
  // each vessel's anchored start), not just the vessel's own fix.
  const routeWaypointsByLeg = useMemo(() => {
    const out = {};
    Object.entries(routeDataByLeg).forEach(([key, data]) => {
      if (data?.waypoints && data.waypoints.length > 1) out[key] = data.waypoints;
    });
    return out;
  }, [routeDataByLeg]);

  // Toggle the on-demand actual-track overlay for one vessel. Fetches a small
  // recent window (default 3 days) only when the user asks, never for the
  // whole fleet. Re-clicking clears it; already-loaded tracks are not refetched.
  const handleToggleTrack = useCallback((vessel) => {
    const vKey = vesselKeyOf(vessel);
    if (!vKey) return;
    setTrackError(null);
    setTracks(prev => {
      if (prev[vKey]) {
        const next = { ...prev };
        delete next[vKey];
        return next;
      }
      return prev;
    });
    // If it was already loaded we just toggled it off above; nothing to fetch.
    if (tracks[vKey]) return;
    if (trackLoading) return;

    const until = new Date();
    const from = new Date(until.getTime() - TRACK_HISTORY_DAYS * 86400000);
    setTrackLoading(vKey);
    globeService.getVesselHistory(
      vessel.imo || null,
      vessel.mmsi || null,
      isoNoMillis(from),
      isoNoMillis(until),
    ).then((history) => {
      const positions = (history?.positions || [])
        .filter(p => p.latitude != null && p.longitude != null)
        .map(p => [p.latitude, p.longitude]);
      if (positions.length < 2) {
        setTrackError({ key: vKey, message: 'No recent track available for this vessel.' });
        return;
      }
      setTracks(prev => ({ ...prev, [vKey]: positions }));
    }).catch(() => {
      setTrackError({ key: vKey, message: 'Could not load the actual track.' });
    }).finally(() => {
      setTrackLoading(null);
    });
  }, [tracks, trackLoading]);

  const handleVesselClick = useCallback((vessel) => {
    if (onVesselClick) onVesselClick(vessel);
  }, [onVesselClick]);

  const handleHover = useCallback((key) => {
    if (onHoverVessel) onHoverVessel(key);
  }, [onHoverVessel]);

  // Port markers carry vesselKey so hover can light up their parent
  // vessel + sister port on the map and the corresponding card.
  const allPortMarkers = useMemo(() => {
    const ports = portMarkers.map(pm => ({ ...pm, vesselKey: pm.vesselKey || null }));
    vessels.forEach(v => {
      const vKey = vesselKeyOf(v);
      (v.ports || []).forEach(p => {
        if (p.lat != null && p.lon != null) {
          ports.push({ ...p, vesselName: v.ship_name, deliveryId: v.deliveryId, vesselKey: vKey });
        }
      });
    });
    return ports;
  }, [vessels, portMarkers]);

  // Frame anchor per vessel: the live longitude folded into the central world —
  // the same anchor that vessel's route line unwraps from. Used to place each
  // port DOT once, in its vessel's frame, instead of stamping it 3x.
  const vesselAnchorByKey = useMemo(() => {
    const anchors = {};
    vessels.forEach(v => {
      if (v.latitude != null && v.longitude != null) {
        anchors[vesselKeyOf(v)] = normalizeLng(v.longitude);
      }
    });
    return anchors;
  }, [vessels]);

  const isEmpty = vessels.length === 0 && portMarkers.length === 0;

  return (
    <div className={`flat-map-container fleet-map-container${discoveryPickActive ? ' flat-map-picking' : ''}`}>
      {isEmpty && (
        <div className="flat-map-message">
          <h3>No Vessels to Display</h3>
          <p>Deliveries with assigned vessels will appear on this map.</p>
        </div>
      )}

      <MapContainer
        ref={mapRef}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        className="flat-map"
        scrollWheelZoom={true}
        worldCopyJump={false}
        maxBoundsViscosity={1.0}
      >
        {/* topleft (under the zoom control): the top-right corner belongs to
            the watchlist/discovery sidebar toggle. */}
        <LayersControl position="topleft">
          {/* Default base is Esri World Street Map: it renders English
              transliterations alongside local scripts at street zooms (CARTO
              rasters are local-language only, e.g. Japanese-only in Japan). */}
          <LayersControl.BaseLayer checked name="Streets (English)">
            <TileLayer
              attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, OpenStreetMap contributors'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={LIGHT_BASE.name}>
            <TileLayer attribution={LIGHT_BASE.attribution} url={LIGHT_BASE.url} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={DARK_BASE.name}>
            <TileLayer attribution={DARK_BASE.attribution} url={DARK_BASE.url} />
          </LayersControl.BaseLayer>
        </LayersControl>

        <FleetMaxBounds />
        <FleetFitBounds vessels={vessels} portMarkers={allPortMarkers} routeWaypointsByLeg={routeWaypointsByLeg} />
        <FleetFlyTo vessels={vessels} flyTrigger={flyTrigger} />
        <FleetFitAll vessels={vessels} portMarkers={allPortMarkers} routeWaypointsByLeg={routeWaypointsByLeg} trigger={fitAllTrigger} />

        <DiscoveryPicker pickActive={discoveryPickActive} onPick={onDiscoveryPick} />
        <DiscoveryFit
          discoveredVessels={discoveredVessels}
          discoveryCenter={discoveryCenter}
          trigger={discoveryFitTrigger}
        />
        <DiscoveryFlyTo discoveryFocus={discoveryFocus} />

        <FleetRoutes
          vessels={vessels}
          onRouteData={handleRouteData}
          hoveredVesselKey={hoveredVesselKey}
          onHoverVessel={handleHover}
        />

        {/* On-demand actual (AIS) tracks: dashed amber breadcrumb, visually
            distinct from the solid blue planned sea route, terminating at the
            live vessel marker. Drawn ONCE, unwrapped into one continuous line
            anchored to the first breadcrumb NORMALIZED into the central world —
            the same central-anchored frame as the planned route, so both extend
            together into the neighbouring copy the ±540 canvas exposes rather
            than being stamped in every world. */}
        {vessels.map(vessel => {
          const vKey = vesselKeyOf(vessel);
          const track = vKey && tracks[vKey];
          if (!track) return null;
          const withMarker = (vessel.latitude != null && vessel.longitude != null)
            ? [...track, [vessel.latitude, vessel.longitude]]
            : track;
          if (withMarker.length < 2) return null;
          const unwrapped = unwrapLngs(normalizeLng(withMarker[0][1]), withMarker);
          // Follows the shared hover key like the route lines: pops for the
          // hovered vessel, recedes while another vessel is highlighted.
          const trackStyle = !hoveredVesselKey
            ? { color: '#f39c12', weight: 2.5, opacity: 0.85, dashArray: '6 6' }
            : vKey === hoveredVesselKey
              ? { color: '#f39c12', weight: 3.5, opacity: 1, dashArray: '6 6' }
              : { color: '#f39c12', weight: 2.5, opacity: 0.2, dashArray: '6 6' };
          return (
            <Polyline
              key={`actual-track-${vKey}`}
              positions={unwrapped}
              pathOptions={trackStyle}
              eventHandlers={{
                mouseover: () => handleHover(vKey),
                mouseout: () => handleHover(null),
              }}
            />
          );
        })}

        {vessels.map(vessel => {
          if (vessel.latitude == null || vessel.longitude == null) return null;
          const vKey = vesselKeyOf(vessel);
          const isHL = vKey && vKey === hoveredVesselKey;
          const heading = vessel.heading || vessel.course || 0;
          const icon = buildVesselIcon(heading, !!vessel.complianceFlagged, isHL);
          const navLabel = NAV_STATUS_LABELS[vessel.nav_status_code] || 'Unknown';

          // Secondary AIS / voyage detail (already on the wire; rendered here).
          const aisEta = fmtDateTime(vessel.eta);
          const dwell = durationSince(vessel.nav_status_since);
          const dwellLabel = dwell ? `${navLabel} ${dwell}` : '';
          const daysAtSea = durationSince(vessel.route_commenced);
          const draughtM = isFinite(Number(vessel.draught)) && Number(vessel.draught) > 0
            ? `${Number(vessel.draught).toFixed(1)} m` : '';
          const loaBeam = (isFinite(Number(vessel.loa)) && Number(vessel.loa) > 0
            && isFinite(Number(vessel.beam)) && Number(vessel.beam) > 0)
            ? `${Number(vessel.loa).toFixed(0)} × ${Number(vessel.beam).toFixed(0)} m` : '';
          // Voyage/SECA nm come from the re-anchored route leg (live -> discharge
          // code), keyed the same way FleetRoutes reports it up.
          const routeLeg = vesselRouteLeg(vessel);
          const routeDist = routeLeg ? fmtRouteDistance(routeDataByLeg[legKey(routeLeg)]) : '';
          // Estimated sailed distance (loading -> live position), from the sailed
          // leg's routed total. The ~ marks it as a routed estimate of distance
          // already covered, not a measured track.
          const sailedLeg = sailedRouteLeg(vessel);
          const sailedData = sailedLeg ? routeDataByLeg[sailedLegKey(sailedLeg)] : null;
          const sailedDist = fmtInt(sailedData?.total_distance);
          const hasVoyageDetail = aisEta || dwellLabel || vessel.destination || vessel.origin
            || daysAtSea || draughtM || loaBeam || routeDist || sailedDist;

          // Tooltip + popup content, built once and rendered into each of the
          // three world-copy markers below.
          const tooltipEl = (
            <Tooltip direction="right" offset={[16, 0]} opacity={0.95} permanent={isHL}>
              <div className="flat-map-speed-label">
                <strong style={{ fontSize: '11px' }}>{vessel.ship_name || 'Unknown'}</strong>
                {vessel.speed != null && <span> {vessel.speed.toFixed(1)} kn</span>}
              </div>
            </Tooltip>
          );
          const popupEl = (
            <Popup>
                <div className="flat-map-popup flat-map-popup-rich">
                  <div className="flat-map-popup-header">
                    <strong>{vessel.ship_name || 'Unknown Vessel'}</strong>
                    <span className="flat-map-popup-badge" style={{ backgroundColor: NAV_STATUS_COLORS[vessel.nav_status_code] || '#6c757d' }}>
                      {navLabel}
                    </span>
                  </div>
                  <div className="flat-map-popup-grid">
                    {vessel.deliveryNumber && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Delivery</span><span>{vessel.deliveryNumber}</span></div>}
                    {vessel.imo && <div className="flat-map-popup-field"><span className="flat-map-popup-label">IMO</span><span>{vessel.imo}</span></div>}
                    {vessel.complianceFlagged
                      ? <div className="flat-map-popup-field"><span className="flat-map-popup-label">R&amp;C</span><span style={{ color: '#dc2626', fontWeight: 600 }}>Sanctioned / not cleared</span></div>
                      : vessel.sanctionsStatus === 'clean'
                        ? <div className="flat-map-popup-field"><span className="flat-map-popup-label">R&amp;C</span><span style={{ color: '#1e7e34', fontWeight: 600 }}>Cleared</span></div>
                        : null}
                    {vessel.speed != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Speed</span><span>{vessel.speed.toFixed(1)} kn</span></div>}
                    {vessel.course != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Course</span><span>{vessel.course.toFixed(1)}&deg;</span></div>}
                    {vessel.product && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Cargo</span><span>{vessel.product}</span></div>}
                    {vessel.destination && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Dest</span><span>{vessel.destination}</span></div>}
                  </div>
                  {hasVoyageDetail && (
                    <div className="flat-map-popup-voyage">
                      {aisEta && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">AIS ETA</span>
                          <span>{aisEta}</span>
                        </div>
                      )}
                      {dwellLabel && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">Status</span>
                          <span>{dwellLabel}</span>
                        </div>
                      )}
                      {vessel.origin && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">Origin</span>
                          <span>{vessel.origin}</span>
                        </div>
                      )}
                      {daysAtSea && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">At sea</span>
                          <span>{daysAtSea}</span>
                        </div>
                      )}
                      {draughtM && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">Draught</span>
                          <span>{draughtM}</span>
                        </div>
                      )}
                      {loaBeam && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">LOA × Beam</span>
                          <span>{loaBeam}</span>
                        </div>
                      )}
                      {routeDist && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">Distance</span>
                          <span>{routeDist}</span>
                        </div>
                      )}
                      {sailedDist && (
                        <div className="flat-map-popup-voyage-row">
                          <span className="flat-map-popup-label">Sailed</span>
                          <span>~{sailedDist} nm</span>
                        </div>
                      )}
                    </div>
                  )}
                  {vessel.position_updated && (
                    <div className="flat-map-popup-updated">
                      Updated: {new Date(vessel.position_updated).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {(vessel.imo || vessel.mmsi) && (
                    <div className="flat-map-popup-track">
                      <button
                        type="button"
                        className="flat-map-track-btn"
                        disabled={trackLoading === vKey}
                        onClick={() => handleToggleTrack(vessel)}
                      >
                        {trackLoading === vKey
                          ? 'Loading track…'
                          : (vKey && tracks[vKey])
                            ? 'Hide actual track'
                            : 'Show actual track'}
                      </button>
                      {trackError && trackError.key === vKey && trackLoading !== vKey && !(vKey && tracks[vKey]) && (
                        <span className="flat-map-track-error">{trackError.message}</span>
                      )}
                    </div>
                  )}
                </div>
            </Popup>
          );

          // The vessel arrow renders ONCE, in the central world — like its
          // route line and port dots. The side world copies are pure spillover
          // canvas for date-line-crossing lines; nothing on this map is ever
          // drawn twice.
          const baseKey = vKey || vessel.imo || vessel.mmsi;
          return (
            <Marker
              key={`vessel-${baseKey}`}
              position={[vessel.latitude, normalizeLng(vessel.longitude)]}
              icon={icon}
              zIndexOffset={isHL ? 1000 : 0}
              eventHandlers={{
                click: () => handleVesselClick(vessel),
                mouseover: () => handleHover(vKey),
                mouseout: () => handleHover(null),
              }}
            >
              {tooltipEl}
              {popupEl}
            </Marker>
          );
        })}

        {allPortMarkers.map((pm, idx) => {
          if (pm.lat == null || pm.lon == null) return null;
          const isHL = pm.vesselKey && pm.vesselKey === hoveredVesselKey;
          // Port dots render ONCE (unlike the vessel arrows, which are stamped
          // into all three world copies). The dot sits in its vessel's route
          // frame: unwrapped relative to the vessel's live longitude — the same
          // anchor the single, continuous route line unwraps from — so a Pacific
          // route's discharge dot lands at +281 exactly where the line ends.
          // Dots with no live parent vessel fold into the central world.
          const anchor = pm.vesselKey != null ? vesselAnchorByKey[pm.vesselKey] : undefined;
          const dotLon = anchor != null
            ? unwrapLng(anchor, pm.lon)
            : normalizeLng(pm.lon);
          // Port label shows only on hover of the dot itself — never permanently.
          // (The green/red dot colour already encodes load vs discharge, so a
          // highlighted vessel's ports stay clean rather than stamping labels
          // permanently across the map.)
          const dotTooltip = (
            <Tooltip direction="bottom" offset={[0, 6]} opacity={0.9}>
              <span className="flat-map-port-label">
                {pm.type === 'loading' ? 'Load' : 'Disch'}: {pm.name || ''}
              </span>
            </Tooltip>
          );
          return (
            <Marker
              key={`port-${idx}-${pm.vesselKey || ''}-${pm.type}`}
              position={[pm.lat, dotLon]}
              icon={buildPortIcon(pm.type, isHL)}
              zIndexOffset={isHL ? 900 : 0}
              eventHandlers={{
                mouseover: () => handleHover(pm.vesselKey),
                mouseout: () => handleHover(null),
              }}
            >
              {dotTooltip}
            </Marker>
          );
        })}

        {/* ── AIS discovery overlay ──────────────────────────────────────
            Search-radius circle + centre crosshair for a "within range" search,
            then every discovered vessel as a distinct magenta diamond. Kept
            entirely separate from the delivery/watchlist markers above. */}
        {discoveryCenter?.lat != null && discoveryCenter?.lon != null && discoveryRadiusKm > 0 && (
          <Circle
            center={[discoveryCenter.lat, discoveryCenter.lon]}
            radius={discoveryRadiusKm * 1000}
            pathOptions={{ color: DISCOVERY_COLOR, weight: 1.5, opacity: 0.6, fillColor: DISCOVERY_COLOR, fillOpacity: 0.06 }}
          />
        )}
        {discoveryCenter?.lat != null && discoveryCenter?.lon != null && (
          <Marker
            position={[discoveryCenter.lat, discoveryCenter.lon]}
            icon={buildDiscoveryCentreIcon()}
            zIndexOffset={500}
          >
            <Tooltip direction="top" offset={[0, -8]} opacity={0.9}>
              <span className="flat-map-port-label">Search centre</span>
            </Tooltip>
          </Marker>
        )}
        {discoveredVessels.map((v, i) => {
          if (v.latitude == null || v.longitude == null) return null;
          const heading = v.heading || v.course || 0;
          const navLabel = NAV_STATUS_LABELS[v.nav_status_code] || v.nav_status || 'Unknown';
          return (
            <Marker
              key={`discovered-${v.imo || v.mmsi || i}`}
              position={[v.latitude, v.longitude]}
              icon={buildDiscoveredIcon(heading)}
              zIndexOffset={400}
            >
              <Tooltip direction="right" offset={[12, 0]} opacity={0.95}>
                <div className="flat-map-speed-label">
                  <strong style={{ fontSize: '11px' }}>{v.ship_name || 'Unknown'}</strong>
                  {v.speed != null && <span> {Number(v.speed).toFixed(1)} kn</span>}
                </div>
              </Tooltip>
              <Popup>
                <div className="flat-map-popup flat-map-popup-rich">
                  <div className="flat-map-popup-header">
                    <strong>{v.ship_name || 'Unknown Vessel'}</strong>
                    <span
                      className="flat-map-popup-badge"
                      style={{ backgroundColor: NAV_STATUS_COLORS[v.nav_status_code] || '#a21caf' }}
                    >
                      Discovered
                    </span>
                  </div>
                  <div className="flat-map-popup-grid">
                    {v.imo && <div className="flat-map-popup-field"><span className="flat-map-popup-label">IMO</span><span>{v.imo}</span></div>}
                    {v.mmsi && <div className="flat-map-popup-field"><span className="flat-map-popup-label">MMSI</span><span>{v.mmsi}</span></div>}
                    {v.ship_type && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Type</span><span>{v.ship_type}</span></div>}
                    {v.flag && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Flag</span><span>{v.flag}</span></div>}
                    {v.speed != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Speed</span><span>{Number(v.speed).toFixed(1)} kn</span></div>}
                    {v.nav_status_code != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Status</span><span>{navLabel}</span></div>}
                    {v.destination && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Dest</span><span>{v.destination}</span></div>}
                    {v.origin && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Origin</span><span>{v.origin}</span></div>}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default FleetMapComponent;
